/**
 * Which auth provider the app mounts.
 *
 * App and AdminApp both need this decision and both used to make it inline, so
 * it lives here once. Normally it is PropelAuth's `RequiredAuthProvider`; when
 * the dev auth bypass is on it is `DevAuthProvider`, which fills the same
 * context from a fixture (see DevAuthProvider.tsx).
 */
import { RequiredAuthProvider } from '@propelauth/react';
import { JSX, ReactNode } from 'react';
import { DevAuthProvider } from './DevAuthProvider';

/**
 * Every condition here is a build-time constant that webpack folds away, so
 * whether a bundle can bypass authentication is decided when it is built and
 * cannot change afterwards. That matters because `VERIFORM_DEV_AUTH_BYPASS` lives
 * in a developer's `.env`, and a `.env` travels further than the machine it was
 * written on.
 *
 * Three of them, independent of each other:
 *
 *   - `NODE_ENV`, which webpack defines from its own `--mode` rather than from
 *     the ambient variable, so it is `production` in the deployed bundle even
 *     though the Dockerfile never sets NODE_ENV;
 *   - `APP_ENV`, which every deploy sets, and which the production one sets to
 *     `production`;
 *   - the flag, which webpack.config.cjs refuses to pass through for a
 *     `--mode production` build at all. That one does not depend on an
 *     environment variable being right, which is why it is worth having on top
 *     of the other two.
 *
 * The flag is compared against one literal because webpack.config.cjs normalises
 * it to `'1'` or `''` (it is the same variable the API reads, so `true` and stray
 * whitespace are accepted -- there rather than here). Comparing literals is what
 * lets webpack fold this to a constant `false` and drop DevAuthProvider from a
 * production bundle altogether, rather than shipping it as unreachable code.
 */
export const isDevAuthBypassEnabled = (): boolean =>
  process.env.NODE_ENV !== 'production' &&
  process.env.APP_ENV !== 'production' &&
  process.env.VERIFORM_DEV_AUTH_BYPASS === '1';

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  if (isDevAuthBypassEnabled()) {
    return <DevAuthProvider>{children}</DevAuthProvider>;
  }

  const authUrl = process.env.PROPEL_AUTH_URL;
  if (!authUrl) {
    throw new Error('PROPEL_AUTH_URL is not defined in environment variables');
  }

  return <RequiredAuthProvider authUrl={authUrl}>{children}</RequiredAuthProvider>;
}
